const COLORS = {
  green: {
    100: '#D7F6E7',
    200: '#209987',
    300: '#08786C',
    400: '#194240',
    500: '#42BAA9',
  },
  blue: {
    100: '#E5F3FE',
    200: '#93c5fd',
    300: '#60a5fa',
  },
  brandPrimary: {
    100: '#303966',
    200: '#242B4D',
    300: '#15192D', // Primary
  },
  yellow: {
    100: '#FFF1D1',
    200: '#FFD426',
    300: '#B95B00',
  },
  red: {
    100: '#FFE4DA',
    200: '#EE533D',
    300: '#C62D2C',
  },
  grey: {
    0: '#FFFFFF',
    100: '#F3F3F2',
    200: '#E7E6E4',
    300: '#CECDCA',
    400: '#A7A6A4',
    500: '#787573',
    600: '#2E2B29',
    700: '#1B1918',
    1000: '#000000',
  },
  orange: {
    100: '#FFEDD5',
    200: '#FED7AA',
    300: '#FDBA74',
  },
  pink: {
    200: '#FBCFE8',
    300: '#F9A8D4',
    400: '#F472N6',
  },
  purple: {
    200: '#E9D5FF',
    300: '#D8B4FE',
    400: '#C084FC',
  },
  white: {
    DEFAULT: 'rgb(240,240,240)',
    1: 'rgba(240, 240, 240, 0.01)',
    2: 'rgba(240, 240, 240, 0.02)',
    3: 'rgba(240, 240, 240, 0.03)',
    4: 'rgba(240, 240, 240, 0.04)',
    5: 'rgba(240, 240, 240, 0.05)',
    6: 'rgba(240, 240, 240, 0.06)',
    7: 'rgba(240, 240, 240, 0.07)',
    8: 'rgba(240, 240, 240, 0.08)',
    9: 'rgba(240, 240, 240, 0.09)',
    10: 'rgba(240, 240, 240, 0.1)',
    20: 'rgba(240, 240, 240, 0.2)',
    30: 'rgba(240, 240, 240, 0.3)',
    40: 'rgba(240, 240, 240, 0.4)',
    50: 'rgba(240, 240, 240, 0.5)',
    60: 'rgba(240, 240, 240, 0.6)',
    70: 'rgba(240, 240, 240, 0.7)',
    80: 'rgba(240, 240, 240, 0.8)',
    90: 'rgba(240, 240, 240, 0.9)',
  },
  black: {
    1: 'rgba(0, 0, 0, 0.01)',
    2: 'rgba(0, 0, 0, 0.02)',
    3: 'rgba(0, 0, 0, 0.03)',
    4: 'rgba(0, 0, 0, 0.04)',
    5: 'rgba(0, 0, 0, 0.05)',
    6: 'rgba(0, 0, 0, 0.06)',
    7: 'rgba(0, 0, 0, 0.07)',
    8: 'rgba(0, 0, 0, 0.08)',
    9: 'rgba(0, 0, 0, 0.09)',
    10: 'rgba(0, 0, 0, 0.1)',
    20: 'rgba(0, 0, 0, 0.2)',
    30: 'rgba(0, 0, 0, 0.3)',
    40: 'rgba(0, 0, 0, 0.4)',
    50: 'rgba(0, 0, 0, 0.5)',
    60: 'rgba(0, 0, 0, 0.6)',
    70: 'rgba(0, 0, 0, 0.7)',
    80: 'rgba(0, 0, 0, 0.8)',
    90: 'rgba(0, 0, 0, 0.9)',
    DEFAULT: '#000000',
  },
}

const DESIGN_SYSTEM_COLOR_VARIANTS = {
  action: {
    property: 'background',
    variants: {
      primary: COLORS.green[300],
      secondary: COLORS.grey[100],
      accent: COLORS.grey[0],
      input: COLORS.grey[1000],
      deemphasized: COLORS.green[200],
    },
  },
  bg: {
    property: 'background',
    variants: {
      transparent: 'transparent',
      primary: COLORS.grey[0],
      emphasis: COLORS.grey[100],
      'accent-landing': COLORS.green[300],
      'accent-avocado': COLORS.green[400],
      'accent-dark': COLORS.grey[700],
      'page-success': COLORS.green[300],
      'page-warning': COLORS.yellow[200],
      'page-error': COLORS.red[200],
      'page-info': COLORS.blue[200],
      'inline-success': COLORS.green[100],
      'inline-warning': COLORS.yellow[100],
      'inline-error': COLORS.red[100],
      'inline-info': COLORS.blue[100],
    },
  },
  border: {
    property: 'border-color',
    variants: {
      primary: COLORS.grey[300],
      secondary: COLORS.grey[200],
      'on-media': COLORS.grey[400],
      focus: COLORS.grey[1000],
      success: COLORS.green[300],
      error: COLORS.red[200],
      transparent: 'transparent',
    },
  },
  text: {
    property: 'color',
    variants: {
      primary: COLORS.grey[1000],
      secondary: COLORS.grey[600],
      tertiary: COLORS.grey[500],
      disabled: COLORS.grey[400],
      'on-media': COLORS.grey[0],
      link: COLORS.green[300],
      'link-deemphasized': COLORS.green[200],
      'semantic-black': COLORS.grey[1000],
      'semantic-white': COLORS.grey[0],
      'semantic-success': COLORS.green[300],
      'semantic-warning': COLORS.yellow[300],
      'semantic-error': COLORS.red[300],
      'semantic-info': COLORS.blue[200],
    },
  },
  icon: {
    property: 'color',
    variants: {
      primary: COLORS.grey[1000],
      secondary: COLORS.grey[600],
      tertiary: COLORS.grey[500],
      disabled: COLORS.grey[400],
      'on-media': COLORS.grey[0],
      link: COLORS.green[300],
      'link-deemphasized': COLORS.green[200],
      'semantic-black': COLORS.grey[1000],
      'semantic-white': COLORS.grey[0],
      'semantic-success': COLORS.green[300],
      'semantic-warning': COLORS.yellow[300],
      'semantic-error': COLORS.red[300],
      'semantic-info': COLORS.blue[200],
    },
  },
  tag: {
    property: 'background',
    variants: {
      'green-light': COLORS.green[100],
      'yellow-light': COLORS.yellow[100],
      'red-light': COLORS.red[100],
      'blue-light': COLORS.blue[100],
      'grey-light': COLORS.grey[100],
      'green-dark': COLORS.green[300],
      'yellow-dark': COLORS.yellow[300],
      'red-dark': COLORS.red[300],
      'blue-dark': COLORS.blue[200],
      'grey-dark': COLORS.grey[1000],
      'grey-medium': COLORS.grey[500],
      'purple-dark': COLORS.purple[400],
      'purple-light': COLORS.purple[200],
      'orange-light': COLORS.orange[100],
      'orange-dark': COLORS.orange[300],
    },
  },
}

const colorPaletteUtility = Object.entries(DESIGN_SYSTEM_COLOR_VARIANTS).reduce((acc, [key, value]) => {
  const { property, variants } = value
  const colorVariantPalette = Object.entries(variants).reduce((acc, [colorKey, colorValue]) => {
    return { ...acc, [`.${key}-${colorKey}`]: { [property]: colorValue } }
  }, {})

  return { ...acc, ...colorVariantPalette }
}, {})

const colorPaletteTwinMacroUtility = Object.entries(DESIGN_SYSTEM_COLOR_VARIANTS).reduce(
  (acc, [name, { variants }]) => {
    return { ...acc, [name]: { ...variants } }
  },
  {},
)

export { colorPaletteTwinMacroUtility, colorPaletteUtility, COLORS }
